/**
 * Lỗi trả về từ API, theo định dạng chuẩn của phản hồi lỗi.
 *
 * Trường `errorCode` là mã lỗi nghiệp vụ do hệ thống đặt. Giao diện xử lý theo mã này
 * chứ không so khớp chuỗi thông báo: thông báo là câu chữ tiếng Việt và sẽ được sửa lại
 * theo góp ý người dùng, còn mã thì không đổi.
 */
export interface ApiProblem {
  readonly title?: string;
  readonly detail?: string;
  readonly status?: number;
  readonly errorCode?: string;

  /** Mã dò vết của yêu cầu, do máy chủ gắn vào mọi phản hồi lỗi. Dùng để tra nhật ký máy chủ. */
  readonly traceId?: string;
}

/** Lỗi phát sinh khi gọi API. */
export class ApiError extends Error {
  public readonly status: number;

  public readonly errorCode: string | undefined;

  public constructor(status: number, message: string, errorCode?: string) {
    super(message);

    this.name = 'ApiError';
    this.status = status;
    this.errorCode = errorCode;
  }

  /** Phiên đăng nhập không còn hiệu lực. */
  public get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

/**
 * Thông báo mặc định khi không đọc được lỗi từ phản hồi.
 *
 * Cố ý không hiển thị dấu vết lỗi kỹ thuật cho người dùng: nó không giúp họ làm gì, và có thể
 * để lộ cấu trúc bên trong hệ thống. Chi tiết nằm ở nhật ký phía máy chủ.
 */
const genericMessage = 'Hệ thống đang gặp sự cố. Vui lòng thử lại hoặc liên hệ quản trị.';

/**
 * Thông báo khi máy chủ gặp lỗi chưa lường trước (mã 5xx không kèm mã lỗi nghiệp vụ).
 *
 * Kèm mã dò vết nếu có: người dùng chép nó gửi quản trị, quản trị tra đúng dòng nhật ký của yêu cầu
 * đó thay vì đoán theo giờ.
 */
function serverFailureMessage(traceId: string | undefined): string {
  const base = 'Máy chủ gặp lỗi khi xử lý yêu cầu. Thử lại sau ít phút';

  return traceId === undefined || traceId.trim() === ''
    ? `${base}; nếu vẫn lỗi, báo quản trị hệ thống.`
    : `${base}; nếu vẫn lỗi, báo quản trị hệ thống kèm mã: ${traceId}`;
}

/** Dựng lỗi từ một phản hồi HTTP không thành công. */
export async function toApiError(response: Response): Promise<ApiError> {
  let problem: ApiProblem | null = null;

  try {
    problem = (await response.json()) as ApiProblem;
  } catch {
    // Phản hồi không phải JSON — dùng thông báo mặc định.
  }

  /*
    Lỗi 5xx chưa lường trước mang `title` tiếng Anh của khung ứng dụng ("An error occurred while
    processing your request."), và ở môi trường phát triển còn mang cả nội dung ngoại lệ trong
    `detail` — cả hai đều không giúp người dùng làm gì và có thể để lộ cấu trúc bên trong. Thay bằng
    câu tiếng Việt chung.

    Ngoại lệ: phản hồi có mã lỗi nghiệp vụ thì `detail` là câu tiếng Việt hệ thống tự đặt (ví dụ mã
    502 khi dịch vụ bên ngoài không trả lời), nên vẫn giữ nguyên.
  */
  if (response.status >= 500 && problem?.errorCode === undefined) {
    return new ApiError(response.status, serverFailureMessage(problem?.traceId));
  }

  const message = problem?.detail ?? problem?.title ?? genericMessage;

  return new ApiError(response.status, message, problem?.errorCode);
}
